import { StateCreator } from 'zustand';
import { getPayors, client } from '../../services/api';
import type { PayorResponse } from '../../services/api/types.gen';

export interface PayorSlice {
  payors: PayorResponse[];
  payorsLoading: boolean;
  payorsError: string | null;
  payorsLastFetched: number | null;
  fetchPayors: () => Promise<void>;
  clearPayors: () => void;
  getPayorById: (id: string) => PayorResponse | undefined;
  getPayorByName: (name: string) => PayorResponse | undefined;
}

export const payorSlice: StateCreator<PayorSlice> = (set, get) => ({
  payors: [],
  payorsLoading: false,
  payorsError: null,
  payorsLastFetched: null,

  fetchPayors: async () => {
    const state = get();
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    if (
      state.payors.length > 0 &&
      state.payorsLastFetched &&
      now - state.payorsLastFetched < fiveMinutes
    ) {
      return;
    }

    try {
      set({ payorsLoading: true, payorsError: null });
      const payorsData = await getPayors({ client });

      if (payorsData && payorsData.data) {
        set({
          payors: payorsData.data,
          payorsLoading: false,
          payorsError: null,
          payorsLastFetched: now,
        });
      } else {
        set({
          payorsLoading: false,
          payorsError: 'No payor data received',
        });
      }
    } catch (error) {
      console.error('Failed to fetch payors:', error);
      set({
        payorsLoading: false,
        payorsError: 'Failed to load payors. Please try again.',
      });
    }
  },

  clearPayors: () => {
    set({
      payors: [],
      payorsLoading: false,
      payorsError: null,
      payorsLastFetched: null,
    });
  },

  getPayorById: (id: string) => {
    const state = get();
    return state.payors.find(payor => payor.id === id);
  },

  getPayorByName: (name: string) => {
    const state = get();
    return state.payors.find(payor =>
      payor.payor_name.toLowerCase().includes(name.toLowerCase())
    );
  },
});
